import {
  CircularProgress,
  Paper,
  TableRow,
  Tooltip,
  IconButton,
  Link
} from "@mui/material";
import Modal from "../base/modal";
import Alert from "../base/alert";
import {
  TableCell,
  Table,
  TableBody,
  TableContainer,
  TableHead
} from "../base/table";
import { BaseButton, BaseTextField } from "../base";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/system";
import React, { useRef, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import UserContext from "../../services/user/user";

import { CSVLink } from "react-csv";

import KeyIcon from "@mui/icons-material/Key";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { URLS } from "../../configuration/urls";

import { FormattedMessage } from "react-intl";

import messages_de from "../../translations/de.json";
import messages_en from "../../translations/en.json";
import messages_es from "../../translations/es.json";
import messages_fr from "../../translations/fr.json";
import {
  ECAxiosAPI,
  EcUserDataContext,
  formattedDateTime
} from "@eclear-ag/ec-common-ui";

export default function AccessKeys() {
  const { setIsAccessKeyErrorVisible, lang } = useContext(UserContext);
  const { userData } = useContext(EcUserDataContext);
  const [tableData, setTableData] = useState([{}, {}, {}, {}, {}]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isgeneratingKey, setIsGeneratingKey] = useState(false);
  const [isDisablingKey, setIsDisablingKey] = useState(false);
  const [isTestingKey, setIsTestingKey] = useState(false);
  const [keyTestResult, setKeyTestResult] = useState("none");

  const [isSecretKeyVisible, setIsSecretKeyVisible] = useState(false);

  const [isDownloadingKey, setIsDownloadingKey] = useState(false);

  const [openModal, setOpenModal] = React.useState(false);
  const [openDisableKeyModal, setOpenDisableKeyModal] = React.useState(false);

  const [secretAccessKey, setSecretAccessKey] = useState("");

  // BaseTextField is react-hook-form based (it renders a Controller), so we keep
  // a minimal rhf form just to drive the secret-key field and mirror its value
  // into `secretAccessKey` — the existing source of truth for the test request.
  const {
    control: testKeyControl,
    formState: { errors: testKeyErrors },
    watch: watchTestKey,
    reset: resetTestKey
  } = useForm({
    mode: "onChange",
    defaultValues: { secretAccessKey: "" }
  });

  const watchedSecretKey = watchTestKey("secretAccessKey");

  useEffect(() => {
    setSecretAccessKey(watchedSecretKey);
  }, [watchedSecretKey]);

  const [openTestKeyModal, setOpenTestKeyModal] = React.useState(false);

  const [keyDetails, setKeyDetails] = useState({
    access_key_id: "",
    access_key_secret: ""
  });

  const messages = {
    de: messages_de,
    en: messages_en,
    es: messages_es,
    fr: messages_fr
  };

  const [selectedKeyDetails, setSelectedKeyDetails] = useState({});

  const tableRef = useRef();

  const modalStyle = {
    position: "absolute",
    top: "25%",
    left: "50%",
    transform: "translate(-50%, -25%)",
    width: "76%"
  };

  const testKey = async (keyId) => {
    setIsTestingKey(true);
    const res = await ECAxiosAPI.get(
      `${URLS.EC_AUTH_BE()}${URLS.TEST_ACCESS_KEY}`,
      {
        access_key_id: keyId,
        access_key_secret: secretAccessKey
      }
    )
      .then((res) => {
        setKeyTestResult("success");
        setIsTestingKey(false);
      })
      .catch((err) => {
        setKeyTestResult("error");
        setIsTestingKey(false);
      });
  };

  const handleCloseModal = () => {
    getKeysList();
    setOpenModal(false);
    setIsSecretKeyVisible(false);
  };
  const handleDisableKeyModal = () => {
    setOpenDisableKeyModal(false);
  };

  const getKeysList = async () => {
    setIsTableLoading(true);
    const keyList = await ECAxiosAPI.get(
      `${URLS.EC_SPOT_BE()}${URLS.LIST_ACCESS_KEY(userData.companyId)}`
    )
      .then((res) => {
        setTableData(res);
        setIsEmpty(false);
        setIsTableLoading(false);
      })
      .catch((err) => {
        setIsEmpty(true);
        setIsTableLoading(false);
      });
  };

  useEffect(() => {
    if (userData.companyId) {
      getKeysList();
    }
  }, [userData]);

  const createAccessKey = async () => {
    setIsGeneratingKey(true);
    const createKey = await ECAxiosAPI.post(
      `${URLS.EC_SPOT_BE()}${URLS.CREATE_ACCESS_KEY(userData.companyId)}`
    )
      .then((res) => {
        setKeyDetails(res);
        setOpenModal(true);
        setIsGeneratingKey(false);
      })
      .catch((err) => {
        setIsAccessKeyErrorVisible(true);
        setIsGeneratingKey(false);
      });
  };

  const disableAccessKey = async (keyId) => {
    setIsDisablingKey(true);
    // setIsGeneratingKey(true);
    const disableKey = await ECAxiosAPI.post(
      `${URLS.EC_SPOT_BE()}${URLS.DISABLE_ACCESS_KEY}${keyId}`
    )
      .then((res) => {
        getKeysList();
        setSelectedKeyDetails({});
        setOpenDisableKeyModal(false);
        setIsDisablingKey(false);
      })
      .catch((err) => {
        console.log("Error disabling key: ", err);
      });
  };

  const emojis = ["🥳​", "​😇", "😊​​", "🎉​​", "👍​​​"];
  const [tooltipText, setTooltipText] = useState("accessKey.copy");
  function ClipboardButton(props) {
    function handleClick() {
      navigator.clipboard.writeText(JSON.stringify(keyDetails));
      setTooltipText("accessKey.copied");
    }

    return (
      <Tooltip
        placement="top"
        arrow
        title={
          <span>
            <FormattedMessage id={tooltipText} />{" "}
            {tooltipText === "accessKey.copied" &&
              emojis[Math.floor(Math.random() * (4 - 0) + 0)]}
          </span>
        }
        onMouseEnter={() => {
          setTooltipText("accessKey.copy");
        }}
      >
        <IconButton
          aria-label="copy-key"
          onClick={handleClick}
          style={props.style}
        >
          <ContentCopyIcon alt="copy icon" style={{ fontSize: "18px" }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box>
      <BaseButton
        aria-label="generate access key"
        startIcon={
          isgeneratingKey ? (
            <CircularProgress size="1.5rem" />
          ) : (
            <KeyIcon size="1.5rem" style={{ transform: "rotate(45deg)" }} />
          )
        }
        variant="filled"
        sx={{ mt: 2, mb: 3 }}
        disabled={isgeneratingKey}
        onClick={() => {
          createAccessKey();
        }}
      >
        <div></div>
        <span style={{ marginLeft: "3px" }}>
          {" "}
          <FormattedMessage id="generateAccessKey" />
        </span>
      </BaseButton>

      <Paper
        sx={{
          backgroundColor: isEmpty ? "rgba(0, 24, 61, 0.08)" : "info.light",
          borderRadius: "10px"
        }}
        className="widget-col-6 widget-row-2 top-countries"
        elevation={0}
      >
        <Box sx={{ textAlign: "center" }} className="card-main-plain">
          {isTableLoading ? (
            <CircularProgress />
          ) : (
            <TableContainer ref={tableRef} style={{ borderRadius: "10px" }}>
              <Table aria-label="access keys table">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        borderRight: "2px solid #e8ebef",
                        border: isEmpty && "2px solid rgba(0, 24, 61, 0.2)",
                        borderLeft: "0px",
                        textAlign: "center",
                        fontWeight: 500,
                        padding: "10px",
                        color: isEmpty && "#b3bac5",
                        borderTop: "0px"
                      }}
                    >
                      <FormattedMessage id="keyId" />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: "2px solid #e8ebef",
                        border: isEmpty && "2px solid rgba(0, 24, 61, 0.2)",
                        textAlign: "center",
                        fontWeight: 500,
                        padding: "10px",
                        color: isEmpty && "#b3bac5",
                        borderTop: "0px"
                      }}
                    >
                      <span style={{ marginLeft: "8px" }}>
                        <FormattedMessage id="createdBy" />
                      </span>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: "2px solid #e8ebef",
                        border: isEmpty && "2px solid rgba(0, 24, 61, 0.2)",
                        textAlign: "center",
                        fontWeight: 500,
                        padding: "10px",
                        color: isEmpty && "#b3bac5",
                        borderTop: "0px"
                      }}
                    >
                      <span style={{ marginLeft: "8px" }}>
                        <FormattedMessage id="createdOn" />
                      </span>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: "2px solid #e8ebef",
                        border: isEmpty && "2px solid rgba(0, 24, 61, 0.2)",
                        textAlign: "center",
                        fontWeight: 500,
                        padding: "10px",
                        color: isEmpty && "#b3bac5",
                        borderTop: "0px"
                      }}
                    >
                      <FormattedMessage id="modifiedBy" />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: "2px solid #e8ebef",
                        border: isEmpty && "2px solid rgba(0, 24, 61, 0.2)",
                        textAlign: "center",
                        fontWeight: 500,
                        padding: "10px",
                        color: isEmpty && "#b3bac5",
                        borderTop: "0px"
                      }}
                    >
                      <FormattedMessage id="modifiedAt" />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: "2px solid #e8ebef",
                        border: isEmpty && "2px solid rgba(0, 24, 61, 0.2)",
                        textAlign: "center",
                        fontWeight: 500,
                        padding: "10px",
                        color: isEmpty && "#b3bac5",
                        borderTop: "0px"
                      }}
                    >
                      <FormattedMessage id="status" />
                    </TableCell>
                    <TableCell
                      sx={{
                        border: isEmpty && "2px solid rgba(0, 24, 61, 0.2)",
                        borderRight: "0px",
                        textAlign: "center",
                        fontWeight: 500,
                        padding: "10px",
                        color: isEmpty && "#b3bac5",
                        borderTop: "0px"
                      }}
                    >
                      <FormattedMessage id="actions" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isEmpty === true ? (
                    <TableRow
                      hover
                      // sx={{ height: " 40px" }}
                      style={{ height: "40px" }}
                    >
                      <TableCell
                        colSpan={7}
                        sx={{
                          // border: "2px solid rgba(0, 24, 61, 0.2)",
                          // borderBottom: "0px",
                          // borderLeft: isEmpty && "0px",
                          textAlign: "center",
                          fontWeight: 400,
                          padding: 0,
                          fontSize: "14px",
                          color: "#687389"
                        }}
                      >
                        <FormattedMessage id="noAccessKeys" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((row, index) => (
                      <TableRow
                        hover
                        key={index}
                        // sx={{ height: " 40px" }}
                        style={{ height: "40px" }}
                      >
                        <TableCell
                          sx={{
                            borderRight: "2px solid #e8ebef",
                            textAlign: "center",
                            fontWeight: 400,
                            padding: "10px",
                            fontSize: "14px"
                          }}
                        >
                          {row.accessKey}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "2px solid #e8ebef",
                            fontWeight: 400,
                            textAlign: "center",
                            padding: "10px",
                            fontSize: "14px"
                          }}
                        >
                          {row.createdBy}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "2px solid #e8ebef",
                            fontWeight: 400,
                            textAlign: "center",
                            padding: "10px",
                            fontSize: "14px"
                          }}
                        >
                          {formattedDateTime(row.createdAt)}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "2px solid #e8ebef",
                            textAlign: "center",
                            fontWeight: 400,
                            padding: "10px",
                            fontSize: "14px"
                          }}
                        >
                          {row.modifiedBy}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "2px solid #e8ebef",
                            textAlign: "center",
                            fontWeight: 400,
                            padding: "10px",
                            fontSize: "14px"
                          }}
                        >
                          {formattedDateTime(row.modifiedAt)}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderRight: "2px solid #e8ebef",
                            textAlign: "center",
                            fontWeight: 500,
                            padding: "10px",
                            fontSize: "14px",
                            color: row.enabled === true ? "#20bf6b" : "#eb3b5a"
                          }}
                        >
                          <FormattedMessage
                            id={row.enabled === true ? "active" : "inactive"}
                          />
                        </TableCell>
                        <TableCell
                          style={{
                            padding: "10px",
                            fontSize: "14px",
                            borderRight: "0px"
                          }}
                        >
                          <Box
                            sx={{
                              justifyContent: "center",
                              display: "flex",
                              alignItems: "center",
                              fontWeight: 500,
                              fontSize: "18px",
                              borderRight: "0px"
                            }}
                          >
                            {row.enabled === true && (
                              <Box
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}
                              >
                                <BaseButton
                                  aria-label="deactivate access key"
                                  onClick={() => {
                                    setSelectedKeyDetails({
                                      id: row.id,
                                      accessKey: row.accessKey
                                    });
                                    setOpenDisableKeyModal(true);
                                  }}
                                  fullWidth
                                  variant="filled"
                                  sx={{ marginRight: "5px" }}
                                >
                                  <FormattedMessage id="deactivate" />
                                </BaseButton>
                                <BaseButton
                                  aria-label="test access key"
                                  onClick={() => {
                                    setSelectedKeyDetails({
                                      id: row.id,
                                      accessKey: row.accessKey
                                    });
                                    resetTestKey({ secretAccessKey: "" });
                                    setOpenTestKeyModal(true);
                                  }}
                                  fullWidth
                                  variant="filled"
                                  sx={{ marginLeft: "5px" }}
                                >
                                  <FormattedMessage id="test" />
                                </BaseButton>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      <Modal
        open={openModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Box sx={{ textAlign: "right" }}>
            <IconButton
              aria-label="add access key modal"
              onClick={handleCloseModal}
            >
              <CloseIcon style={{ color: "var(--text-primary)" }} />
            </IconButton>
          </Box>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              marginTop: "1rem"
            }}
          >
            <Alert severity="error">
              <FormattedMessage id="neverPostKeys" />
            </Alert>
            <Alert severity="success">
              <FormattedMessage id="secretKeyView" />
            </Alert>
            <TableContainer
              ref={tableRef}
              style={{ borderRadius: "10px", marginTop: "1rem" }}
            >
              <Table aria-label="access keys table">
                <TableHead sx={{ borderTop: "2px solid #e8ebef" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        borderRight: "2px solid #e8ebef",
                        borderLeft: "0px",
                        textAlign: "center",
                        fontWeight: 500,
                        padding: "10px",
                        borderTop: "0px"
                      }}
                    >
                      <FormattedMessage id="keyId" />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: "0px",
                        textAlign: "center",
                        fontWeight: 500,
                        padding: "10px",
                        borderTop: "0px"
                      }}
                    >
                      <FormattedMessage id="secretAccessKey" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell
                      sx={{
                        borderLeft: "0px",
                        textAlign: "center",
                        fontWeight: 400,
                        padding: "10px",
                        fontSize: "14px",
                        borderRight: "2px solid #e8ebef"
                      }}
                    >
                      {keyDetails.access_key_id}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderLeft: "0px",
                        textAlign: "center",
                        fontWeight: 400,
                        padding: "10px",
                        fontSize: "14px",
                        display: "flex",
                        justifyContent: "center"
                      }}
                    >
                      {isSecretKeyVisible ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center"
                          }}
                        >
                          {keyDetails.access_key_secret}
                          <ClipboardButton style={{ marginLeft: "18px" }} />
                        </div>
                      ) : (
                        <Box>
                          <span style={{ opacity: "0.5" }}>
                            {keyDetails.access_key_secret.substring(0, 8)}
                            ....{" "}
                          </span>
                          <Link
                            color="primary"
                            variant="contained"
                            sx={{
                              p: "11px 44px",
                              textTransform: "initial",
                              cursor: "pointer"
                            }}
                            onClick={() => {
                              setIsSecretKeyVisible(true);
                            }}
                          >
                            <FormattedMessage id={"showSecretAccessKey"} />
                          </Link>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: "30px"
            }}
          >
            <CSVLink
              style={{ textDecoration: "none" }}
              data={[
                {
                  [messages[lang]["keyId"]]: keyDetails.access_key_id,
                  [messages[lang]["secretAccessKey"]]:
                    keyDetails.access_key_secret
                }
              ]}
              filename={`${messages[lang]["accessKeyDetails"]}_${keyDetails.access_key_id}.csv`}
              target="_blank"
            >
              <BaseButton
                aria-label="download access key"
                startIcon={
                  isDownloadingKey ? (
                    <CircularProgress size="1.5rem" />
                  ) : (
                    <DownloadIcon size="1.5rem" />
                  )
                }
                disabled={isDownloadingKey}
                variant="filled"
                sx={{ p: "11px 44px" }}
              >
                <FormattedMessage id={"downloadCsvFile"} />
              </BaseButton>
            </CSVLink>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={openDisableKeyModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Box sx={{ textAlign: "right" }}>
            <IconButton
              aria-label="add access key modal"
              onClick={handleDisableKeyModal}
            >
              <CloseIcon color="primary" />
            </IconButton>
          </Box>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              marginTop: "1rem"
            }}
          >
            <span>
              <FormattedMessage
                id="confirmDisableKey"
                values={{
                  b: (...chunks) => {
                    return (
                      <span style={{ fontWeight: 600 }}>
                        {selectedKeyDetails.accessKey}
                      </span>
                    );
                  }
                }}
              />
            </span>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              mt: "30px"
            }}
          >
            <BaseButton
              aria-label="cancel"
              variant="text"
              sx={{
                opacity: "0.5",
                p: "11px 44px",
                marginRight: "15px"
              }}
              onClick={() => {
                setOpenDisableKeyModal(false);
              }}
            >
              <FormattedMessage id="cancel" />
            </BaseButton>

            <BaseButton
              aria-label="deactivate access key"
              startIcon={isDisablingKey && <CircularProgress size="1.5rem" />}
              disabled={isDisablingKey}
              variant="filled"
              sx={{
                p: "11px 44px",
                backgroundColor: "var(--error) !important",
                color: "#fff !important"
              }}
              onClick={() => {
                disableAccessKey(selectedKeyDetails.id);
              }}
            >
              <FormattedMessage id="deactivate" />
            </BaseButton>
          </Box>
        </Box>
      </Modal>
      <Modal
        open={openTestKeyModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <Box sx={{ textAlign: "right" }}>
            <IconButton
              aria-label="test access key modal"
              onClick={() => {
                setOpenTestKeyModal(false);
                setKeyTestResult("none");
              }}
            >
              <CloseIcon color="primary" />
            </IconButton>
          </Box>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              marginTop: "1rem"
            }}
          >
            <Box style={{ marginBottom: "15px" }}>
              {keyTestResult === "success" ? (
                <Alert severity="success">
                  <FormattedMessage id="keyTest.success" />
                </Alert>
              ) : keyTestResult === "error" ? (
                <Alert severity="error">
                  <FormattedMessage id="keyTest.error" />
                </Alert>
              ) : keyTestResult === "networkError" ? (
                <Alert severity="error">
                  <FormattedMessage id="keyTest.networkError" />
                </Alert>
              ) : null}
            </Box>
            <span style={{ marginBottom: "20px" }}>
              <FormattedMessage
                id="testModalText"
                values={{
                  b: (...chunks) => {
                    return (
                      <span style={{ fontWeight: 600 }}>
                        {selectedKeyDetails.accessKey}
                      </span>
                    );
                  }
                }}
              />
            </span>
            <BaseTextField
              label="secretAccessKey"
              control={testKeyControl}
              errors={testKeyErrors}
              required
              inputProps={{
                "data-testid": "secret-access-key-input"
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              mt: "30px"
            }}
          >
            <BaseButton
              aria-label="cancel"
              variant="text"
              sx={{
                opacity: "0.5",
                p: "11px 44px",
                marginRight: "15px"
              }}
              onClick={() => {
                setOpenTestKeyModal(false);
                setKeyTestResult("none");
              }}
            >
              <FormattedMessage id="cancel" />
            </BaseButton>

            <BaseButton
              aria-label="test access key"
              startIcon={
                isTestingKey ? <CircularProgress size="1.5rem" /> : null
              }
              disabled={isTestingKey}
              variant="filled"
              sx={{ p: "11px 44px" }}
              onClick={() => {
                testKey(selectedKeyDetails.accessKey);
              }}
            >
              <FormattedMessage id={isTestingKey ? "testing" : "test"} />
            </BaseButton>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
